import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { SearchIcon, PlusIcon, MoreVerticalIcon, EyeIcon, MailIcon, ShieldIcon } from '../components/Icons';
import { IssuePassModal, PassDetailModal, RevokePassModal } from './Passes';
import AddGuestModal from '../components/AddGuestModal';
import toast from 'react-hot-toast';
import './Events.css';

const formatEventDate = (start, end, isMulti) => {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export default function IQACGuests() {
  const { faculty } = useAuth();
  const [arrivedGuests, setArrivedGuests] = useState([]);
  const [upcomingGuests, setUpcomingGuests] = useState([]);
  const [expiredGuests, setExpiredGuests] = useState([]);
  const [revokedGuests, setRevokedGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showIssue, setShowIssue] = useState(false);
  const [filterMode, setFilterMode] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewPass, setViewPass] = useState(null);
  const [revokingPass, setRevokingPass] = useState(null);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');

  const loadData = () => {
    Promise.all([api.get('/passes'), api.get('/events')])
      .then(([passRes, evRes]) => {
        const rawPasses = passRes.data.passes || [];
        // Since all events are auto-approved, we show all passes
        const passes = rawPasses;
        setEvents(evRes.data.events || []);

        const arrived = passes
          .filter(p => p.status === 'used')
          .sort((a, b) => {
            const dateA = a.event?.date ? new Date(a.event.date) : new Date(0);
            const dateB = b.event?.date ? new Date(b.event.date) : new Date(0);
            return dateB - dateA;
          });

        const upcoming = passes
          .filter(p => p.status === 'active' || p.status === 'pending')
          .sort((a, b) => {
            const dateA = a.event?.date ? new Date(a.event.date) : new Date(8640000000000000);
            const dateB = b.event?.date ? new Date(b.event.date) : new Date(8640000000000000);
            return dateA - dateB;
          });

        const expired = passes
          .filter(p => p.status === 'expired')
          .sort((a, b) => {
            const dateA = a.event?.date ? new Date(a.event.date) : new Date(0);
            const dateB = b.event?.date ? new Date(b.event.date) : new Date(0);
            return dateB - dateA;
          });

        const revoked = passes
          .filter(p => p.status === 'revoked')
          .sort((a, b) => {
            const dateA = a.event?.date ? new Date(a.event.date) : new Date(0);
            const dateB = b.event?.date ? new Date(b.event.date) : new Date(0);
            return dateB - dateA;
          });

        setArrivedGuests(arrived);
        setUpcomingGuests(upcoming);
        setExpiredGuests(expired);
        setRevokedGuests(revoked);
      })
      .catch(err => console.error('Error loading guests:', err))
      .finally(() => setLoading(false));
  };

  const handleResend = async (id) => {
    try {
      await api.post(`/passes/${id}/resend`);
      toast.success('Email sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email failed');
    }
  };
  
  const handleRevoke = (passIdOrPass) => {
    // Determine the pass object
    const passObj = typeof passIdOrPass === 'string' 
      ? [...upcomingGuests, ...arrivedGuests, ...expiredGuests, ...revokedGuests].find(p => p._id === passIdOrPass) 
      : passIdOrPass;
    setRevokingPass(passObj);
  };

  const confirmRevoke = async (id, reason) => {
    try {
      await api.delete(`/passes/${id}`, { data: { reason: reason || 'No reason provided' } });
      toast.success('Pass revoked');
      setRevokingPass(null);
      setViewPass(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revoke pass');
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  }

  const guests = activeTab === 'upcoming' ? upcomingGuests : (activeTab === 'arrived' ? arrivedGuests : (activeTab === 'revoked' ? revokedGuests : expiredGuests));
  const filteredGuests = search.trim()
    ? guests.filter(g => {
        const s = search.toLowerCase();
        const d = g.event?.date ? new Date(g.event.date) : null;
        const dateStrNumeric = d ? d.toLocaleDateString('en-IN') : '';
        const dateStrFull = d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).toLowerCase() : '';
        return (
          g.guestName?.toLowerCase().includes(s) ||
          g.guestEmail?.toLowerCase().includes(s) ||
          g.organization?.toLowerCase().includes(s) ||
          g.passCode?.toLowerCase().includes(s) ||
          g.event?.title?.toLowerCase().includes(s) ||
          dateStrNumeric.includes(s) ||
          dateStrFull.includes(s)
        );
      })
    : guests;

  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
  const isAO = faculty?.role === 'ao';
  const pageSubtitle = (isAdmin || isAO) ? 'Manage guest invites and arrivals' : 'Manage your personal invites and arrivals';

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Guest Passes</div>
          <div className="page-subtitle">{pageSubtitle}</div>
        </div>
        <div className="page-actions">
          {faculty?.role !== 'ao' && (
            <button type="button" className="btn btn-navy" onClick={() => setShowAddGuest(true)}>
              + Add Guest
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Search */}
        <div className="card toolbar" style={{ marginBottom: '1rem' }}>
          <input
            className="toolbar-input"
            placeholder="Search by name, email, organisation…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Tabs */}
        <div className="tabs-container" style={{ marginBottom: '1.5rem' }}>
          <button
            type="button"
            className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            Active ({upcomingGuests.length})
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'arrived' ? 'active' : ''}`}
            onClick={() => setActiveTab('arrived')}
          >
            Used ({arrivedGuests.length})
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'expired' ? 'active' : ''}`}
            onClick={() => setActiveTab('expired')}
          >
            Expired ({expiredGuests.length})
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'revoked' ? 'active' : ''}`}
            onClick={() => setActiveTab('revoked')}
          >
            Revoked ({revokedGuests.length})
          </button>
        </div>

        {/* Guests List */}
        <div className="card">
          {filteredGuests.length === 0 ? (
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
              <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                {search ? 'No matching guests' : `No ${activeTab} guests`}
              </div>
              <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>
                {!search && (
                  activeTab === 'upcoming' ? 'Issue invites to get started' : 
                  activeTab === 'arrived' ? 'No guests have used their passes yet' :
                  activeTab === 'revoked' ? 'No revoked passes found' :
                  'No expired passes found'
                )}
              </div>
            </div>
          ) : (
            <div className="table-wrap" style={{ overflow: 'visible' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: '180px' }}>Guest</th>
                    <th>Organisation</th>
                    <th>Invite Code</th>
                    <th style={{ minWidth: '200px' }}>Event</th>
                    <th>Event Date</th>
                    <th>Status</th>
                    <th>Issued on</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.map(g => (
                    <tr key={g._id} style={{ position: 'relative', zIndex: openMenuId === g._id ? 50 : 1 }}>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer' }}>
                        <div className="cell-stack-title">{g.guestName}</div>
                        <div className="cell-stack-muted">{g.guestEmail}</div>
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                        {g.organization || '—'}
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer' }}>
                        <span className="code-pill-table">{g.passCode}</span>
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer' }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-2)' }}>{g.event?.title}</div>
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                        {formatEventDate(g.event?.date, g.event?.endDate, g.event?.isMultiDay)}
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer' }}>
                        <span className={`badge badge-${g.status}`}>{g.status}</span>
                      </td>
                      <td onClick={() => setViewPass(g)} style={{ cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>
                        {formatEventDate(g.createdAt)}
                      </td>
                      <td>
                        <div className="action-dropdown-container">
                          <button
                            type="button"
                            className={`action-dropdown-trigger ${openMenuId === g._id ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === g._id ? null : g._id);
                            }}
                          >
                            <MoreVerticalIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                          </button>
                          
                          {openMenuId === g._id && (
                            <>
                              <div className="action-menu-overlay" onClick={() => setOpenMenuId(null)} />
                              <div className="action-dropdown-menu">
                                <button className="action-dropdown-item" onClick={() => { setViewPass(g); setOpenMenuId(null); }}>
                                  <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                                  View Details
                                </button>
                                {!isAO && (
                                  <>
                                    <button 
                                      className="action-dropdown-item" 
                                      onClick={() => { handleResend(g._id); setOpenMenuId(null); }}
                                      disabled={g.status === 'used' || g.status === 'expired' || g.status === 'revoked'}
                                    >
                                      <MailIcon style={{ width: '1rem', height: '1rem' }} />
                                      Resend Invite
                                    </button>
                                    <div style={{ borderTop: '1px solid var(--border-1)', margin: '0.25rem 0' }} />
                                    <button
                                      className="action-dropdown-item danger"
                                      onClick={() => { handleRevoke(g._id); setOpenMenuId(null); }}
                                      disabled={g.status === 'revoked' || g.status === 'used' || g.status === 'expired'}
                                    >
                                      <ShieldIcon style={{ width: '1rem', height: '1rem' }} />
                                      Revoke Pass
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showIssue && (
        <IssuePassModal
          events={events}
          onClose={() => setShowIssue(false)}
          onIssued={() => { setShowIssue(false); loadData(); }}
        />
      )}
      {viewPass && (
        <PassDetailModal 
          pass={viewPass} 
          onClose={() => setViewPass(null)} 
          onResend={handleResend} 
          onRevoke={handleRevoke}
          readOnly={isAO}
        />
      )}
      {revokingPass && (
        <RevokePassModal 
          pass={revokingPass} 
          onClose={() => setRevokingPass(null)} 
          onRevoke={confirmRevoke} 
        />
      )}
      {showAddGuest && (
        <AddGuestModal
          faculty={faculty}
          onClose={() => setShowAddGuest(false)}
          onSaved={() => {
            loadData();
            setShowAddGuest(false);
          }}
        />
      )}
    </div>
  );
}
